import { UIBrandedFare, UIFlightData, UIPriceBreakdown } from "@flights/types";
import { t } from "@bookingcom/lingojs-core";
import React, { useCallback, useEffect, useMemo } from "react";
import { Text, Title } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import useFormatPrice from "../../../../hooks/useFormatPrice";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price";
import Frame from "components/elements/Frame";
import styles from "./FareCardPrice.module.css";
import {
  getCfarTicketFullPrice,
  getCfarTicketPricePerPerson,
  getFlexibleTicketFullPrice,
  getFlexibleTicketPricePerPerson
} from "../../CheckoutTicketTypeInner/ticketTypeUtils";
import { isOfMetaOrigin } from "utils/marketing-url-params";
import useUserAgent from "hooks/useUserAgent";
import useRouteName from "hooks/useRouteName";
import useGetSearchResultsBound from "hooks/useGetSearchResultsBound";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { useTrackUnifiedPriceTag } from "utils/experiments/pricing/flights_unified_price_tag";
import { UnifiedPriceTag, UnifiedPriceTagProps } from "components/elements/UnifiedPriceTag";

type FareCardPriceProps = {
  fareOffer: UIBrandedFare;
  baseOffer: UIFlightData;
  isFlexTicket?: boolean;
  isCfarTicket?: boolean;
  isBold?: boolean;
};

const FareCardPrice = (props: FareCardPriceProps) => {
  const { fareOffer, baseOffer, isFlexTicket: isFlexTicket, isCfarTicket, isBold } = props;
  const i18n = useI18n();
  const { formatPrice } = useFormatPrice();

  const routeName = useRouteName();
  const { isMobile } = useUserAgent();
  const { trackUnifiedPriceTag, trackUnifiedPriceTagStages } = useTrackUnifiedPriceTag(); // flights_unified_price_tag

  const adultTravellerPrice = getLowestAdultTravellerPrice(fareOffer.travellerPrices);
  const isRoundPrice = !isOfMetaOrigin() && isMobile;

  const totalPrice = useMemo(() => {
    if (isFlexTicket && baseOffer.offerExtras?.flexibleTicket) {
      return formatPrice(getFlexibleTicketFullPrice(baseOffer).total);
    }
    if (isCfarTicket && baseOffer && baseOffer.ancillaries?.cancelForAnyReason) {
      const cfarTotal = getCfarTicketFullPrice(baseOffer);
      return cfarTotal ? formatPrice(cfarTotal.total) : formatPrice(fareOffer.priceBreakdown.total);
    }
    return isRoundPrice && fareOffer.priceBreakdown.totalRounded
      ? formatPrice(fareOffer.priceBreakdown.totalRounded, { decimalPlaces: 0 })
      : formatPrice(fareOffer.priceBreakdown.total);
  }, [
    fareOffer.priceBreakdown.total,
    fareOffer.priceBreakdown.totalRounded,
    formatPrice,
    isRoundPrice,
    isFlexTicket,
    isCfarTicket,
    baseOffer
  ]);

  const bound = useGetSearchResultsBound();

  const getBoundFormattedPrice = useCallback(
    (base: UIPriceBreakdown, fare: UIPriceBreakdown) => {
      if (isRoundPrice && fare.totalRounded && base.totalRounded) {
        return formatPrice(pricingUtils.subtractPrice(fare.totalRounded, base.totalRounded), { decimalPlaces: 0 });
      }

      const finalPrice = pricingUtils.subtractPrice(fare.total, base.total);
      const formattedFinalPrice = formatPrice(finalPrice).replace("-", "");

      return finalPrice.units < 0
        ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedFinalPrice } }))
        : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedFinalPrice } }));
    },
    [formatPrice, i18n, isRoundPrice]
  );

  const ariaAttributes = useMemo(() => {
    const attributes = {};

    let perPersonPrice = adultTravellerPrice
      ? formatPrice(adultTravellerPrice.travellerPriceBreakdown.total)
      : formatPrice(fareOffer.priceBreakdown.total);

    const totalPriceLabel = i18n.trans(t("a11y_flights_apex_price_per_traveller"));

    const perTravellerPriceLabel = `${totalPriceLabel}, ${i18n.trans(
      t("flights_sr_total_inc_price", { variables: { total_price: totalPrice } })
    )}`;

    const label = fareOffer.travellerPrices.length > 1 ? perTravellerPriceLabel : totalPriceLabel;

    if (bound === "inbound" && adultTravellerPrice) {
      perPersonPrice = getBoundFormattedPrice(adultTravellerPrice.travellerPriceBreakdown, fareOffer.priceBreakdown);
    }

    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    attributes["aria-label"] = `${perPersonPrice} ${label}`;

    return attributes;
  }, [
    adultTravellerPrice,
    formatPrice,
    fareOffer.priceBreakdown,
    fareOffer.travellerPrices.length,
    i18n,
    totalPrice,
    bound,
    getBoundFormattedPrice
  ]);

  const eachTravellerPrice = useMemo(() => {
    if (adultTravellerPrice) {
      if (isFlexTicket) {
        const flexibleTicket = baseOffer.offerExtras?.flexibleTicket;
        const adultTravellerPrice = getLowestAdultTravellerPrice(baseOffer.travellerPrices)?.travellerPriceBreakdown;
        const pricePerPerson = getFlexibleTicketPricePerPerson(flexibleTicket?.priceBreakdown, adultTravellerPrice);

        if (bound === "inbound" && adultTravellerPrice) {
          return getBoundFormattedPrice(pricePerPerson, adultTravellerPrice);
        }

        if (isRoundPrice && pricePerPerson.totalRounded) {
          return formatPrice(pricePerPerson.totalRounded, { decimalPlaces: 0 });
        }
        return formatPrice(pricePerPerson.total);
      }
      if (isCfarTicket) {
        const cfarTicket = baseOffer.ancillaries?.cancelForAnyReason;
        const adultTravellerPrice = getLowestAdultTravellerPrice(baseOffer.travellerPrices)?.travellerPriceBreakdown;
        const pricePerPerson =
          cfarTicket && getCfarTicketPricePerPerson(cfarTicket.priceBreakdown, adultTravellerPrice);

        if (bound === "inbound" && adultTravellerPrice && pricePerPerson) {
          return getBoundFormattedPrice(pricePerPerson, adultTravellerPrice);
        }

        if (cfarTicket && pricePerPerson) {
          if (isRoundPrice && pricePerPerson.totalRounded) {
            return formatPrice(pricePerPerson.totalRounded, { decimalPlaces: 0 });
          }
        }
        return pricePerPerson && formatPrice(pricePerPerson.total);
      }

      if (bound === "inbound" && adultTravellerPrice) {
        const adultTravellerPrice = getLowestAdultTravellerPrice(baseOffer.travellerPrices)?.travellerPriceBreakdown;
        const fareAdultTravellerPrice = getLowestAdultTravellerPrice(
          fareOffer.travellerPrices
        )?.travellerPriceBreakdown;

        if (adultTravellerPrice && fareAdultTravellerPrice) {
          return getBoundFormattedPrice(adultTravellerPrice, fareAdultTravellerPrice);
        }
      }

      return isRoundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? formatPrice(adultTravellerPrice.travellerPriceBreakdown.totalRounded, { decimalPlaces: 0 })
        : formatPrice(adultTravellerPrice.travellerPriceBreakdown.total);
    } else {
      return (
        <div className={styles.priceContainer}>
          {isRoundPrice && fareOffer.priceBreakdown.totalRounded
            ? formatPrice(fareOffer.priceBreakdown.totalRounded, { decimalPlaces: 0 })
            : formatPrice(fareOffer.priceBreakdown.total)}
        </div>
      );
    }
  }, [
    adultTravellerPrice,
    isFlexTicket,
    isCfarTicket,
    bound,
    isRoundPrice,
    formatPrice,
    baseOffer.offerExtras?.flexibleTicket,
    baseOffer.travellerPrices,
    baseOffer.ancillaries?.cancelForAnyReason,
    getBoundFormattedPrice,
    fareOffer.travellerPrices,
    fareOffer.priceBreakdown.totalRounded,
    fareOffer.priceBreakdown.total
  ]);

  const shouldHidePriceExplanation = useMemo<boolean>(() => {
    const isSolo = [...fareOffer.travellerPrices].length === 1;
    if (
      ![
        "searchresults",
        "flightdetails",
        /* start - flights_split_bounds_us */
        "searchresults-outbound",
        "searchresults-inbound",
        "flightdetails-outbound",
        "flightdetails-inbound"
        /* end - flights_split_bounds_us */
      ].includes(routeName) ||
      !isSolo
    ) {
      return false;
    }
    return true;
  }, [fareOffer.travellerPrices, routeName]);

  // flights_unified_price_tag -- start
  // ==========================================================================
  const shouldShowUnifiedPriceTag = useMemo(() => trackUnifiedPriceTag(), [trackUnifiedPriceTag]);

  const priceTagProps = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag) return;

    const ticketPrice = (() => {
      if (isFlexTicket && baseOffer.offerExtras?.flexibleTicket) {
        return getFlexibleTicketFullPrice(baseOffer);
      }
      if (isCfarTicket && baseOffer.ancillaries?.cancelForAnyReason) {
        const cfarTotal = getCfarTicketFullPrice(baseOffer);
        return cfarTotal || fareOffer.priceBreakdown;
      }
      return fareOffer.priceBreakdown;
    })();

    const price = isRoundPrice && ticketPrice.totalRounded ? ticketPrice.totalRounded : ticketPrice.total;

    const strikethroughPrice = fareOffer.priceBreakdown.showPriceStrikethrough
      ? isRoundPrice && fareOffer.priceBreakdown.totalWithoutDiscountRounded
        ? fareOffer.priceBreakdown.totalWithoutDiscountRounded
        : fareOffer.priceBreakdown.totalWithoutDiscount
      : undefined;

    const strikethroughPricePosition = !!strikethroughPrice && isMobile ? "top" : "end";

    return {
      price,
      strikethroughPrice,
      strikethroughPricePosition,
      shouldRoundPrice: isRoundPrice
    };
  }, [
    shouldShowUnifiedPriceTag,
    isFlexTicket,
    isCfarTicket,
    baseOffer,
    fareOffer.priceBreakdown,
    isRoundPrice,
    isMobile
  ]);

  const priceTagPropsWithPricePerAdult = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag || !priceTagProps) return;

    if (!adultTravellerPrice) return priceTagProps;

    const multipleTravellers = fareOffer.travellerPrices.length > 1;

    const pricePerAdult = (() => {
      if (isFlexTicket && baseOffer.offerExtras?.flexibleTicket) {
        const baseAdultTravellerPrice = getLowestAdultTravellerPrice(
          baseOffer.travellerPrices
        )?.travellerPriceBreakdown;
        const pricePerPersonFlex = getFlexibleTicketPricePerPerson(
          baseOffer.offerExtras.flexibleTicket.priceBreakdown,
          baseAdultTravellerPrice
        );
        return isRoundPrice && pricePerPersonFlex.totalRounded
          ? pricePerPersonFlex.totalRounded
          : pricePerPersonFlex.total;
      }
      if (isCfarTicket && baseOffer.ancillaries?.cancelForAnyReason) {
        const baseAdultTravellerPrice = getLowestAdultTravellerPrice(
          baseOffer.travellerPrices
        )?.travellerPriceBreakdown;
        const pricePerPersonCfar = getCfarTicketPricePerPerson(
          baseOffer.ancillaries.cancelForAnyReason.priceBreakdown,
          baseAdultTravellerPrice
        );
        return isRoundPrice && pricePerPersonCfar.totalRounded
          ? pricePerPersonCfar.totalRounded
          : pricePerPersonCfar.total;
      }
      return isRoundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
        : adultTravellerPrice.travellerPriceBreakdown.total;
    })();

    if (bound === "inbound") {
      const baseAdultTravellerPrice = getLowestAdultTravellerPrice(baseOffer.travellerPrices)?.travellerPriceBreakdown;
      const fareAdultTravellerPrice = getLowestAdultTravellerPrice(fareOffer.travellerPrices)?.travellerPriceBreakdown;

      if (baseAdultTravellerPrice && fareAdultTravellerPrice) {
        let finalPricePerAdult;
        if (isRoundPrice && fareAdultTravellerPrice.totalRounded && baseAdultTravellerPrice.totalRounded) {
          finalPricePerAdult = pricingUtils.subtractPrice(
            fareAdultTravellerPrice.totalRounded,
            baseAdultTravellerPrice.totalRounded
          );
        } else {
          finalPricePerAdult = pricingUtils.subtractPrice(fareAdultTravellerPrice.total, baseAdultTravellerPrice.total);
        }

        const pricePerAdultTranslations = [
          (formattedPrice: string) => formattedPrice.replace("-", ""),
          (formattedPrice: string) =>
            finalPricePerAdult.units < 0
              ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
              : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
        ];

        return {
          ...priceTagProps,
          pricePerAdult: finalPricePerAdult,
          pricePerAdultTranslations,
          shouldShowPricePerAdult: !!adultTravellerPrice
        };
      }
    }

    return {
      ...priceTagProps,
      pricePerAdult,
      shouldShowPricePerAdult: multipleTravellers && !!adultTravellerPrice
    };
  }, [
    shouldShowUnifiedPriceTag,
    priceTagProps,
    adultTravellerPrice,
    isFlexTicket,
    isCfarTicket,
    baseOffer,
    fareOffer.travellerPrices,
    isRoundPrice,
    bound,
    i18n
  ]);

  const unifiedPriceTagPriceProps = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag || !priceTagPropsWithPricePerAdult) return;

    const pricePropsVariant = isBold ? "strong_2" : "strong_1";
    const pricePropsAttributes = {
      "aria-hidden": true,
      "data-testid": "flight_card_price_main_price"
    };

    return {
      ...priceTagPropsWithPricePerAdult,
      priceAlignment: "start",
      priceProps: {
        attributes: pricePropsAttributes,
        tagName: "div",
        variant: pricePropsVariant
      }
    };
  }, [shouldShowUnifiedPriceTag, priceTagPropsWithPricePerAdult, isBold]);

  const unifiedPriceTagExplanationProps = useMemo((): Partial<UnifiedPriceTagProps> | undefined => {
    if (!shouldShowUnifiedPriceTag) return;

    if (adultTravellerPrice) {
      const pricePerAdultExplanationProps: UnifiedPriceTagProps["pricePerAdultExplanationProps"] = {
        variant: "small_1",
        attributes: {
          "aria-hidden": true,
          "data-testid": "flight_card_price_total_price"
        }
      };

      const pricePerAdultExplanationTranslations = [];

      if (bound === "inbound") {
        pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_split_bounds_us_total", { variables: { price: formattedPrice } }))
        );
      } else {
        if (!shouldHidePriceExplanation) {
          pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
            fareOffer.travellerPrices.length > 1
              ? i18n.trans(t("flights_sr_total_inc_price", { variables: { total_price: formattedPrice } }))
              : i18n.trans(t("flights_price_qualifier_total_for_all"))
          );
        }
      }

      return {
        pricePerAdultExplanationProps,
        pricePerAdultExplanationTranslations
      };
    }
    return;
  }, [
    shouldShowUnifiedPriceTag,
    adultTravellerPrice,
    bound,
    i18n,
    fareOffer.travellerPrices.length,
    shouldHidePriceExplanation
  ]);

  useEffect(() => {
    trackUnifiedPriceTagStages({
      withPriceBreakdownTooltip: false,
      withPriceStrikethrough:
        fareOffer.priceBreakdown.showPriceStrikethrough &&
        (isRoundPrice && fareOffer.priceBreakdown.totalWithoutDiscountRounded
          ? true
          : !!fareOffer.priceBreakdown.totalWithoutDiscount),
      withPricePerAdultExplanation: fareOffer.travellerPrices.length > 1 && !!adultTravellerPrice,
      withPriceExplanation: false,
      withPriceExplanationOnly: false,
      withUsOutboundInbound: !!bound
    });
  }, [
    adultTravellerPrice,
    bound,
    fareOffer.priceBreakdown.showPriceStrikethrough,
    fareOffer.priceBreakdown.totalWithoutDiscount,
    fareOffer.priceBreakdown.totalWithoutDiscountRounded,
    fareOffer.travellerPrices.length,
    isRoundPrice,
    trackUnifiedPriceTagStages
  ]);
  // ==========================================================================
  // flights_unified_price_tag -- end

  return (
    <div>
      {bound === "inbound" && (
        <Text variant="small_1" color="neutral_alt">
          {fareOffer.travellerPrices.length > 1
            ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
            : i18n.trans(t("flights_sr_split_bounds_us_round_trip"))}
        </Text>
      )}

      {shouldShowUnifiedPriceTag && unifiedPriceTagPriceProps ? (
        <UnifiedPriceTag
          {...unifiedPriceTagPriceProps}
          {...unifiedPriceTagExplanationProps}
          attributes={ariaAttributes}
        />
      ) : (
        <>
          <Frame direction="row" justifyContent="flex-start" attributes={ariaAttributes}>
            <Title
              variant={isBold ? "strong_2" : "strong_1"}
              attributes={{ "aria-hidden": true, "data-testid": "flight_card_price_main_price" }}
              className={styles.mainPrice}
              titleTagName="div"
              title={eachTravellerPrice}
            />
          </Frame>

          {bound === "inbound" ? (
            <Text
              variant="small_1"
              className={styles.totalPriceLabel}
              attributes={{ "aria-hidden": true, "data-testid": "flight_card_price_total_price" }}
            >
              {i18n.trans(
                t("flights_sr_split_bounds_us_total", {
                  variables: { price: totalPrice }
                })
              )}
            </Text>
          ) : (
            !shouldHidePriceExplanation && (
              <Text
                variant="small_1"
                className={styles.totalPriceLabel}
                attributes={{ "aria-hidden": true, "data-testid": "flight_card_price_total_price" }}
              >
                <div className={styles.priceContainer}>
                  {fareOffer.travellerPrices.length > 1
                    ? i18n.trans(
                        t("flights_sr_total_inc_price", {
                          variables: { total_price: totalPrice }
                        })
                      )
                    : i18n.trans(t("flights_price_qualifier_total_for_all"))}
                </div>
              </Text>
            )
          )}
        </>
      )}
    </div>
  );
};

export default FareCardPrice;
